import axios from 'axios';

/**
 * Vuex state factory function for initializing the store state.
 * 
 * @returns {Object} - The initial state of the store.
 * - seriesSelected: The currently selected series (default is null).
 * - racesList: An array to hold the list of races (default is an empty array).
 * - resultsList: An array to hold the results list (default is an empty array).
 * - bSeriesResults: A boolean flag indicating if the series results are being processed (default is false).
 * - raceSelected: The currently selected race (default is null).
 */
const state = () => ({
  seriesSelected: null,
  racesList: [],
  resultsList: [],
  resultsListAll: [],
  bSeriesResults: false,
  raceSelected: -1
});

/**
 * Vuex getters to access state properties.
 */
const getters = {
  /**
   * Get the currently selected series.
   * @param {Object} state - The current state of the store.
   * @returns {Object|null} - The selected series or null if none is selected.
   */
  getSeriesSelected: (state) => state.seriesSelected,

  /**
   * Get the list of races.
   * @param {Object} state - The current state of the store.
   * @returns {Array} - The list of races.
   */
  getRacesList: (state) => state.racesList,

  /**
   * Get the list of results.
   * @param {Object} state - The current state of the store.
   * @returns {Array} - The list of results.
   */
  getResultsList: (state) => state.resultsList,

  /**
   * Get the boolean flag indicating if the series results are being processed.
   * @param {Object} state - The current state of the store.
   * @returns {Boolean} - The flag indicating if the series results are being processed.
   */
  getBSeriesResults: (state) => state.bSeriesResults,
  getResultsListAll: (state) => state.resultsListAll,
  getRaceSelected: (state) => state.raceSelected
};

/**
 * Vuex mutations to update the state.
 */
const mutations = {
  /**
   * Update the currently selected series.
   * @param {Object} state - The current state of the store.
   * @param {Object} data - The new series data to set as selected.
   */
  updateSelectedSerie(state, data) {
    state.seriesSelected = data;
  },

  /**
   * Set the list of races.
   * @param {Object} state - The current state of the store.
   * @param {Array} data - The array of race data to set.
   */
  setRacesList(state, data) {
    state.racesList = data; 
  },

  /**
   * Set the list of results.
   * @param {Object} state - The current state of the store.
   * @param {Array} data - The array of results data to set.
   */
  setResultsList(state, data) {
    state.resultsList = data;
  },

  /**
   * Set the boolean flag indicating if the series results are being processed.
   * @param {Object} state - The current state of the store.
   * @param {Boolean} data - The new boolean value to set.
   */
  setBSeriesResults(state, data) {
    state.bSeriesResults = data;
  },
  setResultsListAll(state,data) {
    state.resultsListAll = data;
  },
  setRaceSelected(state,data) {
    state.raceSelected = data;
  }
}

const actions = {
  async importScoresAction(context, data) {
    const { raceId } = data;
    await axios.get(`${process.env.VUE_APP_ROOT}/laravel/public/api/v1/promoter/series/import/${raceId}`);
  },
  async getRacesListAction(context, seriesId) {
    if(seriesId) {
      const response = await axios.get(`${process.env.VUE_APP_ROOT}/laravel/public/api/v1/promoter/series/${seriesId}/table/race`);
      
      let rows = response.data;
      context.commit('setRacesList', rows);
    }
  },
  async getResultsAllAction(context, seriesId){
    if(seriesId) {
      const response = await axios.get(`${process.env.VUE_APP_ROOT}/laravel/public/api/v1/promoter/series/${seriesId}/results`);
      
      if (response != undefined) {
        context.commit('setResultsListAll', response.data.message.data);
      }
    }
  },
  async getResultsAction(context, { seriesId, raceId }) {
    if (seriesId) {
      if(typeof raceId == 'undefined') {
        raceId = context.getters['getRaceSelected'];
      }
      
      const url =
        raceId == -1 ?
          `/api/v1/promoter/series/${seriesId}/results` :
          `/api/v1/promoter/series/${seriesId}/race/${raceId}/results`;
          
      const response = await axios.get(`${process.env.VUE_APP_ROOT}/laravel/public${url}`);
      
      if (response != undefined) {
        context.commit('setResultsList', response.data.message.data);
      }
    }
  }
};

export default {
  state, 
  getters, 
  mutations, 
  actions
}
